/**
 * Shared field utilities — SINGLE SOURCE OF TRUTH
 *
 * Used by all providers, frontends, and the generation worker.
 * Ensures field name sanitization and placeholder tree building
 * are consistent across the entire codebase.
 */

import type { TemplatePlaceholder, PlaceholderNode } from './api'

/**
 * Sanitize a field name into a valid template variable name.
 *
 * Rules:
 * - Spaces and dots → underscore (coalesced)
 * - Non-letter, non-number, non-combining-mark, non-underscore → underscore
 * - Preserves Unicode (Chinese, Arabic, Thai, Vietnamese, etc.)
 *   \p{L} = letters, \p{N} = numbers, \p{M} = combining marks (Thai vowels, Arabic diacritics, etc.)
 */
export function sanitizeFieldName(name: string): string {
  return name
    .replace(/[\s.]+/g, '_')
    .replace(/[^\p{L}\p{N}\p{M}_]/gu, '_')
}

/** Map TemplatePlaceholder.type → PlaceholderNode.fieldType */
function mapPlaceholderType(type: TemplatePlaceholder['type']): PlaceholderNode['fieldType'] {
  switch (type) {
    case 'variable': return 'text'
    case 'loop': return 'loop'
    case 'conditional': return 'conditional'
    case 'image': return 'image'
    case 'qr': return 'qr'
    case 'barcode': return 'barcode'
    case 'attachment': return 'attachment'
    case 'signature': return 'signature'
  }
}

function isListOrText(fieldType: PlaceholderNode['fieldType']): boolean {
  return fieldType === 'loop' || fieldType === 'text'
}

/**
 * Build a hierarchical placeholder tree from a flat TemplatePlaceholder[] list.
 * Groups children under their parent by dot-path prefix.
 *
 * - `Items` (loop) + `Items.Product` (variable) → Items has nested [Items.Product]
 * - `Warehouse.Address` (variable, no explicit parent) → implicit Warehouse (linked) with nested [Warehouse.Address]
 * - Top-level fields remain flat
 */
export function buildPlaceholderTree(placeholders: TemplatePlaceholder[]): PlaceholderNode[] {
  // Separate top-level from nested (has dot in name)
  const topLevel: TemplatePlaceholder[] = []
  const nested: TemplatePlaceholder[] = []

  for (const p of placeholders) {
    if (p.name.includes('.')) {
      nested.push(p)
    } else {
      topLevel.push(p)
    }
  }

  // Top-level lists and plain values by name — the nodes that can own children. A
  // conditional, image or code on the same name is its own entry and never takes a list's
  // place here; if it did, the list's columns would hang off the `{#if}`.
  const nodeMap = new Map<string, PlaceholderNode>()
  // …and the last of any other kind, which owns dotted fields only when no list or value
  // carries the name (`{#if Client}{Client.Name}{/if}`), as it always has.
  const otherNodes = new Map<string, PlaceholderNode>()
  const result: PlaceholderNode[] = []

  for (const p of topLevel) {
    const fieldType = mapPlaceholderType(p.type)

    // A list is one field however often the template uses it. `{#Items}…{/Items}` and
    // `{Items | sum:"Price"}` arrive as a loop and a variable of the same name; as two
    // nodes the children hung off whichever came last, leaving an empty "Items (loop)"
    // beside an "Items (text)" that owned the columns. (Top level only: the validator
    // reports a nested list's uses under different names.)
    const existing = isListOrText(fieldType) ? nodeMap.get(p.name) : undefined
    if (existing) {
      if (fieldType === 'loop') existing.fieldType = 'loop'
      continue
    }

    const node: PlaceholderNode = {
      variable: p.name,
      fieldType,
    }
    if (p.operator) node.operator = p.operator
    if (p.compareValue !== undefined) node.compareValue = p.compareValue
    if (isListOrText(fieldType)) nodeMap.set(p.name, node)
    else otherNodes.set(p.name, node)
    result.push(node)
  }

  // Group nested placeholders by their first path segment
  const childrenByParent = new Map<string, TemplatePlaceholder[]>()

  for (const p of nested) {
    const dotIdx = p.name.indexOf('.')
    const parentKey = p.name.substring(0, dotIdx)

    if (!childrenByParent.has(parentKey)) {
      childrenByParent.set(parentKey, [])
    }
    childrenByParent.get(parentKey)!.push(p)
  }

  // Attach children to existing parents, or create implicit linked parents
  for (const [parentKey, children] of childrenByParent) {
    let parentNode = nodeMap.get(parentKey) ?? otherNodes.get(parentKey)

    if (!parentNode) {
      // Create implicit linked parent (e.g., Warehouse.Address → Warehouse is linked)
      parentNode = { variable: parentKey, fieldType: 'linked' }
      nodeMap.set(parentKey, parentNode)
      result.push(parentNode)
    }

    // Recursively build children (they might have deeper nesting like A.B.C)
    const childPlaceholders = children.map((c): TemplatePlaceholder => ({
      ...c,
      // Keep full path for the child variable name
    }))

    if (!parentNode.nested) parentNode.nested = []

    // Check if children have further nesting (e.g., Items.SubList.Field)
    const directChildren: TemplatePlaceholder[] = []
    const deeperChildren: TemplatePlaceholder[] = []

    for (const c of childPlaceholders) {
      const rest = c.name.substring(parentKey.length + 1) // Remove "Parent."
      if (rest !== '.' && rest.includes('.')) {
        deeperChildren.push(c)
      } else {
        directChildren.push(c)
      }
    }

    // Add direct children as nodes
    for (const c of directChildren) {
      const node: PlaceholderNode = {
        variable: c.name,
        fieldType: mapPlaceholderType(c.type),
      }
      if (c.operator) node.operator = c.operator
      if (c.compareValue !== undefined) node.compareValue = c.compareValue
      parentNode.nested.push(node)
    }

    // Recurse for deeper children
    if (deeperChildren.length > 0) {
      const deepTree = buildPlaceholderTree(deeperChildren.map(c => ({
        ...c,
        // Strip parent prefix for recursive grouping, then restore full path
        name: c.name.substring(parentKey.length + 1),
      })))
      // Restore full paths in the result
      const restoreFullPaths = (nodes: PlaceholderNode[], prefix: string): void => {
        for (const n of nodes) {
          n.variable = `${prefix}.${n.variable}`
          if (n.nested) restoreFullPaths(n.nested, prefix)
        }
      }
      restoreFullPaths(deepTree, parentKey)
      parentNode.nested.push(...deepTree)
    }

    // Clean up: remove empty nested arrays
    if (parentNode.nested.length === 0) {
      delete parentNode.nested
    }
  }

  return result
}
